export class UIElement {
  #element;         // Proprietà privata dell'elemento DOM
  #originalDisplay; // Ricorderà se l'elemento era block, flex, grid, ecc.

  /**
   * Crea un'istanza di UIElement.
   * @param {string|null} [id=null] - L'ID dell'elemento HTML nel DOM (opzionale).
   */
  constructor( id = null ) {
    if (id != null) {
      this.#element = document.getElementById(id);
      
      if (this.#element) {
        // Controlliamo se l'elemento ha attributi o classi che lo nascondono all'avvio
        const hasHiddenAttr = this.#element.hasAttribute('hidden');
        const hasDNoneClass = this.#element.classList.contains('d-none');

        // Forza temporaneamente la visibilità solo per leggere il display REALE del CSS
        if (hasHiddenAttr) this.#element.removeAttribute('hidden');
        if (hasDNoneClass) this.#element.classList.remove('d-none');

        // Adesso getComputedStyle leggerà "flex", "grid", ecc. e non "none"
        const computedDisplay = window.getComputedStyle(this.#element).display;
        this.#originalDisplay = computedDisplay === 'none' ? 'block' : computedDisplay;

        // Ripristina lo stato iniziale di invisibilità
        if (hasHiddenAttr) this.#element.setAttribute('hidden', '');
        if (hasDNoneClass) this.#element.classList.add('d-none');

      }
    }
  }

  /**
   * Mostra l'elemento istanziato e aggiorna opzionalmente il testo dello span interno.
   * @param {string|null} [message] - Il nuovo testo da mostrare nello span.
   */
  show() {
    if (!this.#element) return;
    
    this.#element.removeAttribute('hidden'); 
    this.#element.classList.remove('d-none'); 
    this.#element.style.setProperty('display', this.#originalDisplay, 'important');
  }

  /**
   * Nasconde l'elemento istanziato e resetta il testo dello span su 'Running'.
   */
  hide() {
    if (!this.#element) return;
    this.#element.setAttribute('hidden', '');
    this.#element.style.setProperty('display', 'none', 'important'); 

  }

  /**
   * Alterna la visibilità dell'elemento istanziato.
   * @param {boolean} [state] - Se true mostra, se false nasconde. Se omesso, fa il toggle automatico.
   */
  toggle(state) {
    if (!this.#element) return;

    if (state === undefined) {
      // Se non passi uno stato, fa il toggle automatico basandosi sulla visibilità attuale
      const isHidden = this.#element.hasAttribute('hidden') || window.getComputedStyle(this.#element).display === 'none';
      isHidden ? this.show() : this.hide();
    } else {
      state ? this.show() : this.hide();
    }
  }

  // =========================================================================
  // METODI GENERICI (LAVORANO VIA PARAMETRO ESTERNO)
  // =========================================================================

  /**
   * Mostra un qualsiasi elemento nel DOM tramite il suo ID.
   * @param {string} tagId - L'ID dell'elemento da mostrare.
   */
  showByName(tagId) {
    const el = document.getElementById(tagId);
    if (el) {
      el.removeAttribute('hidden'); 
      el.classList.remove('d-none');
      el.style.removeProperty('display');
    }
  }

  /**
   * Nasconde un qualsiasi elemento nel DOM tramite il suo ID.
   * @param {string} tagId - L'ID dell'elemento da nascondere.
   */
  hideByName(tagId) {
    const el = document.getElementById(tagId);
    if (el) {
      el.setAttribute('hidden', '');
      el.style.setProperty('display', 'none', 'important');
    }
  }

  /**
   * Aggiunge la classe 'active' a un elemento tramite il suo ID.
   * @param {string} tagId - L'ID dell'elemento da attivare.
   */
  activate(tagId) {
    document.getElementById(tagId)?.classList.add('active');
  }

  /**
   * Aggiunge una classe CSS all'elemento istanziato.
   * @param {string} className - Nome della classe da aggiungere.
   */
  addClass(className) { 
    this.#element?.classList.add(className); 
  }

  /**
   * Rimuove una classe CSS dall'elemento istanziato.
   * @param {string} className - Nome della classe da rimuovere.
   */
  removeClass(className) { 
    this.#element?.classList.remove(className); 
  }
}

// CON FIREBASE AUTHENTICATION
export async function vercelAsyncOnAuth(auth, endpoint, sendData) {
  // 1. Verifichiamo che l'utente sia loggato
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error("Devi essere loggato per effettuare questa operazione.");
  }

  // 2. RECUPERIAMO IL TOKEN (la riga che mancava!)
  const token = await user.getIdToken();

  // 3. Facciamo la chiamata
  const response = await fetch(`/api/${endpoint}`, {
    method: 'POST',
    headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
    },
    body: JSON.stringify(sendData)
  });

  // 4. Se la risposta NON è ok
    if (!response.ok) {
      const textError = await response.text(); // Leggiamo il corpo della risposta come testo grezzo
      
      try {
        const jsonError = JSON.parse(textError); // Proviamo a convertirlo in JSON
        if (jsonError && jsonError.message) {
          throw new Error(jsonError.message); // ✅ Se c'è il messaggio del backend, usa questo!
        }
      } catch (e) {
        // Se non era un JSON valido, controlliamo se l'errore è quello lanciato sopra
        if (e.message && !e.message.includes("Unexpected token")) {
          throw e; 
        }
      }

      // Fallback: se il server ha risposto con testo semplice (o HTML di errore di Vercel)
      throw new Error(`Errore Server ${response.status}: ${textError || response.statusText}`);
    }

    return await response.json(); 
  }


export async function vercelAsync(endpoint, sendData) {
  const response = await fetch(`/api/${endpoint}`, {
    method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(sendData) // Trasforma l'oggetto in testo per il viaggio
      });

  if (!response.ok) {
    throw new Error(`Errore Server: ${response.status}`);
  }

  return await response.json(); // Restituisce l'oggetto JSON pronto all'uso
}


